/**
 * Types for transcript-based suggested query chips.
 *
 * These chips provide real-time Evidence query suggestions based on
 * the live consultation transcript.
 *
 * Categories are mapped to the clinical workflow phases:
 * - History (Hx): Questions to ask the patient
 * - Examination (Ex): Physical examination findings
 * - Assessment (Ax): Clinical reasoning, symptom exploration, safety signals
 * - Management (Mx): Investigations and treatment plan
 */

import type { LucideIcon } from 'lucide-react';
import {
  CircleAlert,
  ClipboardList,
  FileText,
  FlaskConical,
  HelpCircle,
  Search,
  Stethoscope,
  Target,
  TrendingUp,
} from 'lucide-react';

import colors from 'tailwindcss/colors';

import { bark, forest, sand, skies } from '@heidi/design-tokens';

import type { SuggestedChip, SuggestedChipCategory } from '@heidi/evidence/contracts';

export type { SuggestedChip, SuggestedChipCategory };

// ============================================================================
// Clinical Workflow Types
// ============================================================================

/**
 * Clinical workflow phases based on the SOAP/HEAP model:
 * - hx: History - Patient history and questions
 * - ex: Examination - Physical examination
 * - ax: Assessment - Clinical reasoning and diagnosis
 * - mx: Management - Investigations and treatment plan
 */
export type ClinicalWorkflowPhase = 'hx' | 'ex' | 'ax' | 'mx';

/**
 * Higher-order category groups for UI labeling
 */
export interface WorkflowPhaseConfig {
  label: string;
  description: string;
}

export const WORKFLOW_PHASE_CONFIG: Record<ClinicalWorkflowPhase, WorkflowPhaseConfig> = {
  hx: { label: 'Ask', description: 'History-taking prompts' },
  ex: { label: 'Examine', description: 'Physical assessment' },
  ax: { label: 'Consider', description: 'Clinical reasoning' },
  mx: { label: 'Act', description: 'Management actions' },
};

/**
 * Transcription state for status indicator
 */
export type TranscriptionState = 'idle' | 'transcribing' | 'paused' | 'completed';

/**
 * Status colors for chip states
 */
export const STATUS_COLORS = {
  ready: { bg: skies[100], border: skies[500], text: skies[700] },
  viewed: { bg: forest[50], border: forest[300], text: forest[600] },
  pinned: { bg: sand[100], border: bark[300], text: bark[800] },
} as const;

/**
 * Status light colors for Evidence button
 */
export const STATUS_LIGHT_COLORS = {
  idle: bark[400],
  transcribing: forest[500],
  paused: '#f59e0b', // amber-500
} as const;

/**
 * Status of an auto-queried chip
 */
/**
 * Query status for chips:
 * - generating: First time query sent, waiting for answer
 * - ready: Answer available but not viewed yet
 * - viewed: User has seen the answer (hovered on preview)
 * - updating: User clicked to refresh with new transcript context
 */
export type AutoQueryStatus = 'generating' | 'ready' | 'viewed' | 'updating' | 'error';

/**
 * Category styling using Heidi design system colors:
 * - All chips: bark border and text (using inline styles for primitives not in Tailwind)
 * - Normal: sand background
 * - Red flags: red background (validation-destructive-muted)
 */

export interface CategoryStyleConfig {
  bgColor: string; // CSS color value (fallback for non-destructive)
  textColor: string; // CSS color value (fallback for non-destructive)
  borderColor: string; // CSS color value (fallback for non-destructive)
  label: string;
  description: string; // Short explanation of what this category provides
  icon: LucideIcon; // Lucide icon component for the category
  workflowPhase: ClinicalWorkflowPhase; // Clinical workflow phase mapping
  isDestructive?: boolean; // Use validation-destructive design system colors
}

export const categoryConfig: Record<SuggestedChipCategory, CategoryStyleConfig> = {
  // Safety-critical (Assessment phase) - uses soft destructive styling (muted bg, red text, no border)
  red_flag: {
    bgColor: colors.red[50], // validation-destructive-muted
    textColor: colors.red[500], // validation-destructive
    borderColor: 'transparent', // no border
    label: 'Safety warnings',
    description: 'Urgent safety concerns that need immediate attention',
    icon: CircleAlert,
    workflowPhase: 'ax',
    isDestructive: true,
  },

  // History phase - Questions to ask the patient
  suggested_question: {
    bgColor: sand[100],
    textColor: bark[800],
    borderColor: bark[300],
    label: 'Questions to ask',
    description: 'Questions to gather more clinical information',
    icon: HelpCircle,
    workflowPhase: 'hx',
  },

  // Examination phase - Physical assessment
  examination: {
    bgColor: sand[100],
    textColor: bark[800],
    borderColor: bark[300],
    label: 'Exam findings',
    description: 'Physical exam findings to look for',
    icon: Stethoscope,
    workflowPhase: 'ex',
  },

  // Assessment phase - Clinical reasoning
  symptom_exploration: {
    bgColor: sand[100],
    textColor: bark[800],
    borderColor: bark[300],
    label: 'Symptom exploration',
    description: 'Symptom-led differentials and distinguishing features',
    icon: Search,
    workflowPhase: 'ax',
  },
  condition: {
    bgColor: sand[100],
    textColor: bark[800],
    borderColor: bark[300],
    label: 'Condition',
    description: 'Specific conditions relevant to the presentation',
    icon: Target,
    workflowPhase: 'ax',
  },

  // Management phase - Investigations and plan
  investigation: {
    bgColor: sand[100],
    textColor: bark[800],
    borderColor: bark[300],
    label: 'Suggested workup',
    description: 'Recommended tests and investigations',
    icon: FlaskConical,
    workflowPhase: 'mx',
  },
  plan: {
    bgColor: sand[100],
    textColor: bark[800],
    borderColor: bark[300],
    label: 'Treatment planning',
    description: 'Treatment and management recommendations',
    icon: ClipboardList,
    workflowPhase: 'mx',
  },

  // Clinical review (patient-context mode)
  clinical_review: {
    bgColor: sand[100],
    textColor: bark[800],
    borderColor: bark[300],
    label: 'Clinical review',
    description: 'Clinical trajectory patterns and past presentation review',
    icon: TrendingUp,
    workflowPhase: 'ax',
  },

  // Document drafting from clinical tasks (peek sheet / chips experiment)
  document_draft: {
    bgColor: sand[100],
    textColor: bark[800],
    borderColor: bark[300],
    label: 'Document draft',
    description: 'Draft letters and patient-facing documents from this session',
    icon: FileText,
    workflowPhase: 'mx',
  },
};

/**
 * Category priority for ordering (lower = more important, anchored left)
 *
 * Order follows clinical workflow while preserving safety-first:
 * 1. Safety-critical (always first)
 * 2. History phase (Hx)
 * 3. Examination phase (Ex)
 * 4. Assessment phase (Ax)
 * 5. Management phase (Mx)
 */
export const CATEGORY_PRIORITY: Record<SuggestedChipCategory, number> = {
  // Safety-critical (always first)
  red_flag: 0,
  // History phase
  suggested_question: 1,
  // Examination phase
  examination: 2,
  // Assessment phase
  symptom_exploration: 3,
  condition: 4,
  clinical_review: 5,
  // Management phase
  investigation: 6,
  plan: 7,
  document_draft: 8,
};

/**
 * Categories exposed in settings / filter toggles (subset of {@link SuggestedChipCategory}).
 * `as const` so `.map` narrows keys and `t(\`settings.evidence.category.*\`)` stays type-safe.
 */
export const ALL_CATEGORIES = [
  'red_flag',
  'symptom_exploration',
  'suggested_question',
  'investigation',
  'examination',
  'plan',
] as const;

/**
 * Category order for marketplace display (grouped by clinical workflow).
 * Different from CATEGORY_PRIORITY which is for chip sorting.
 */
export const CATEGORY_ORDER: SuggestedChipCategory[] = [
  'suggested_question',
  'symptom_exploration',
  'red_flag',
  'examination',
  'investigation',
  'plan',
];

/**
 * Get the workflow phase for a category
 */
export function getCategoryWorkflowPhase(
  category: SuggestedChipCategory
): ClinicalWorkflowPhase {
  return categoryConfig[category].workflowPhase;
}

/**
 * Get the icon component for a category
 */
export function getCategoryIcon(category: SuggestedChipCategory): LucideIcon {
  return categoryConfig[category].icon;
}

/**
 * Group categories by workflow phase
 */
export function getCategoriesByWorkflowPhase(): Record<
  ClinicalWorkflowPhase,
  SuggestedChipCategory[]
> {
  const grouped: Record<ClinicalWorkflowPhase, SuggestedChipCategory[]> = {
    hx: [],
    ex: [],
    ax: [],
    mx: [],
  };

  for (const [category, config] of Object.entries(categoryConfig)) {
    grouped[config.workflowPhase].push(category as SuggestedChipCategory);
  }

  return grouped;
}

/**
 * Sort chips by priority: red_flag first, then by category priority, then by relevance
 */
export function sortChipsByPriority(chips: SuggestedChip[]): SuggestedChip[] {
  return [...chips].sort((a, b) => {
    // First sort by category priority
    const priorityDiff = CATEGORY_PRIORITY[a.category] - CATEGORY_PRIORITY[b.category];
    if (priorityDiff !== 0) return priorityDiff;

    // Within same category, sort by relevance (higher first)
    const relevanceA = a.relevance ?? 0.5;
    const relevanceB = b.relevance ?? 0.5;
    return relevanceB - relevanceA;
  });
}

/**
 * Peek sheet ordering: when `documentDraftsFirst`, all `document_draft` chips are listed
 * before other chips (each group still sorted by {@link sortChipsByPriority}).
 * Used for the document-suggestion `chips` experiment so task-based drafts lead the row.
 */
export function sortPeekSheetSuggestedChips(
  chips: SuggestedChip[],
  documentDraftsFirst: boolean
): SuggestedChip[] {
  if (!documentDraftsFirst) {
    return sortChipsByPriority(chips);
  }
  const drafts = chips.filter((c) => c.category === 'document_draft');
  if (drafts.length === 0) {
    return sortChipsByPriority(chips);
  }
  const rest = chips.filter((c) => c.category !== 'document_draft');
  return [...sortChipsByPriority(drafts), ...sortChipsByPriority(rest)];
}

/**
 * Calculate scale factor based on relevance (1.0 to 1.2 range)
 */
export function getRelevanceScale(relevance: number = 0.5): number {
  // Map 0-1 relevance to 1.0-1.2 scale (max 20% bigger)
  return 1 + relevance * 0.2;
}

/**
 * Calculate opacity based on relevance (0.7 to 1.0 range)
 */
export function getRelevanceOpacity(relevance: number = 0.5): number {
  // Map 0-1 relevance to 0.7-1.0 opacity
  return 0.7 + relevance * 0.3;
}

/**
 * Carousel configuration
 */
export interface CarouselConfig {
  visibleCount: number; // Number of chips visible by default (3)
  expandOnHover: boolean; // Show all chips on hover
  enableDismiss: boolean; // Allow users to dismiss chips
  enableKeyboardNav: boolean; // Arrow key navigation
  autoQueryEnabled: boolean; // Auto-query chips above threshold
  autoQueryThreshold: number; // Relevance threshold for auto-query (0-1)
}
